import type { ButtonProps } from '@neo/primitives/button'
import { type ReactNode, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'router-v6'
import { regionToCountry } from 'v2/configs/constants'
import { ButtonWithPermission } from 'v2/shared/components/button-with-permission'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { getLocalAuth } from 'v2/utils'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { COMPLIANCE_APPLICATION_NUMBER_TYPE } from './compliance/compliance.constants'

/**
 * Starts a Business KYC application: records the start, then opens the create
 * form wherever the caller hosts it (the page's sheet, or a dialog of its own).
 */
export const useStartKyc = (openCreateForm: () => void) =>
  useCallback(() => {
    track(AnalyticsEvents.COMPLIANCE_FLOW_STARTED, {
      country: regionToCountry(getLocalAuth().region),
      number_type: COMPLIANCE_APPLICATION_NUMBER_TYPE,
    })
    openCreateForm()
  }, [openCreateForm])

// How off-page CTAs land on a KYC screen with a flow open (`?action=<action>`). The param goes once
// `onAction` runs, so Back does not open the flow again.
export const useActionDeepLink = (action: string, onAction: () => void, isReady = true) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isRequested = isReady && searchParams.get('action') === action
  // Read when it fires, so a callback that is new each render cannot fire it twice.
  const onActionRef = useRef(onAction)
  onActionRef.current = onAction
  useEffect(() => {
    if (!isRequested) return
    onActionRef.current()
    setSearchParams(
      (prev) => {
        prev.delete('action')
        return prev
      },
      { replace: true },
    )
  }, [isRequested, setSearchParams])
}

export const useCreateDeepLink = (onCreate: () => void, isReady = true) =>
  useActionDeepLink('create', onCreate, isReady)

interface StartKycButtonProps {
  onClick: () => void
  testId: string
  className?: string
  size?: ButtonProps['size']
  variant?: ButtonProps['variant']
  label?: ReactNode
  /** What the KYC behind it requires; today's is submitted with the create permission. */
  permission?: { module: RbacModules; type: RbacPermissionTypes }
}

const SUBMIT_KYC_PERMISSION = { module: RbacModules.channelCalls, type: RbacPermissionTypes.create }

/** "Start KYC", gated on the permission that lets someone submit one. */
export const StartKycButton = ({
  onClick,
  testId,
  className,
  size,
  variant,
  label = 'Start KYC',
  permission = SUBMIT_KYC_PERMISSION,
}: StartKycButtonProps) => (
  <ButtonWithPermission
    className={className}
    size={size}
    variant={variant}
    module={permission.module}
    permissionType={permission.type}
    onClick={onClick}
    disabledReason="You don't have permission to submit KYC."
    data-testid={testId}
  >
    {label}
  </ButtonWithPermission>
)
